import { useState, type FormEvent } from 'react';
import { Button, Field, Input, MessageBar, MessageBarBody, Spinner, Text, makeStyles } from '@fluentui/react-components';
import { reissueByEmailRequestSchema } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { requestReissueByEmail, requestReissueFromToken } from './api.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  button: { minHeight: '2.75rem', alignSelf: 'flex-start' },
});

export interface ReissueRequestProps {
  /** The link the person arrived with, held in memory only. Null: the session cookie is the proof. */
  readonly token: string | null;
}

type State =
  | { readonly kind: 'idle'; readonly error: string | null }
  | { readonly kind: 'sending' }
  | { readonly kind: 'done'; readonly message: string };

/**
 * "Email me a new link" (#82, ADR-004): the recovery that replaces an HR support ticket.
 *
 * Whatever is asked, the answer is the same sentence from the API, and this component never
 * decorates it with anything that would suggest the address was or was not recognised — the
 * server went to some trouble to make that impossible to tell, and the page must not undo it.
 */
export function ReissueRequest({ token }: ReissueRequestProps) {
  const styles = useStyles();
  const [state, setState] = useState<State>({ kind: 'idle', error: null });
  const [email, setEmail] = useState('');

  async function ask(send: () => Promise<string | null>) {
    setState({ kind: 'sending' });
    const message = await send();
    setState(message === null ? { kind: 'idle', error: 'That did not go through. Please try again.' } : { kind: 'done', message });
  }

  function submitEmail(event: FormEvent) {
    event.preventDefault();
    if (!reissueByEmailRequestSchema.safeParse({ email }).success) {
      setState({ kind: 'idle', error: 'Check the email address and try again.' });
      return;
    }
    void ask(() => requestReissueByEmail(email));
  }

  if (state.kind === 'done') {
    return (
      <div className={styles.block}>
        <MessageBar intent="success">
          <MessageBarBody>{state.message}</MessageBarBody>
        </MessageBar>
        <Text>If nothing arrives, contact our office.</Text>
      </div>
    );
  }

  const busy = state.kind === 'sending';
  return (
    <div className={styles.block}>
      {state.kind === 'idle' && state.error !== null ? (
        <MessageBar intent="error">
          <MessageBarBody>{state.error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <Text>Anything you had already typed is still saved.</Text>
      <Button className={styles.button} appearance="primary" disabled={busy} onClick={() => void ask(() => requestReissueFromToken(token))}>
        {busy ? <Spinner size="tiny" label="Sending" labelPosition="after" /> : 'Email me a new link'}
      </Button>

      {/* noValidate: our own check answers in the same words on every browser. */}
      <form className={styles.form} onSubmit={submitEmail} noValidate>
        <Text>No longer have your link? Enter the email address we have for you.</Text>
        <Field label="Email address">
          <Input type="email" autoComplete="email" value={email} onChange={(_, d) => setEmail(d.value)} disabled={busy} />
        </Field>
        <Button className={styles.button} appearance="secondary" type="submit" disabled={busy || email.trim() === ''}>
          Send a new link
        </Button>
      </form>
    </div>
  );
}
